import {
  getModuleRoutingSettingsSnapshot as getCoreModuleRoutingSettingsSnapshot,
  getModuleSettingsKey as getCoreModuleSettingsKey,
  isModuleRoutingEnabled,
  isModuleRouteEnabled,
  resolveModuleBasePath,
  resolveModuleRoutePath,
} from '@/app-router/module-routing';
import type { ModuleRegistryEntry } from '@/app-router/types';
import { MODULE_ROUTE_NAMES as MODULE_ROUTE_NAME_SET } from '@/app-router/citizen/module-route-names.js';

/**
 * Citizen modules whose primary routes are driven by API settings
 * (`citizen_module_{id}_enabled` / `citizen_module_{id}_path`).
 * See ADR-0002.
 */
export const MODULE_REGISTRY: ModuleRegistryEntry[] = [
  {
    id: 'my_cases',
    routes: [
      { name: 'my-cases-v2', component: 'my-cases-v2.vue', defaultPath: '/mina/arenden' },
      {
        name: 'my-cases-v2-deeplink',
        component: 'my-cases-v2.vue',
        defaultPath: '/mina/arenden/:caseKey',
        pathSuffix: '/:caseKey',
      },
    ],
  },
  {
    id: 'my_children',
    routes: [
      { name: 'my-children', component: 'my-children.vue', defaultPath: '/mina/barn' },
      {
        name: 'my-child',
        component: 'my-child.vue',
        defaultPath: '/mina/barn/:personUid',
        pathSuffix: '/:personUid',
      },
    ],
  },
  {
    id: 'my_eservices',
    routes: [{ name: 'my-eservices', component: 'my-eservices.vue', defaultPath: '/mina/etjanster' }],
  },
  {
    id: 'my_associations',
    routes: [{ name: 'my-associations', component: 'my-associations.vue', defaultPath: '/mina/foreningar' }],
  },
  {
    id: 'my_educations',
    routes: [{ name: 'my-educations', component: 'my-educations.vue', defaultPath: '/mina/utbildningar' }],
  },
  {
    id: 'my_employments',
    routes: [
      { name: 'my-employments', component: 'my-employments.vue', defaultPath: '/mina/anstallningar' },
      {
        name: 'preboarding',
        component: 'preboarding.vue',
        defaultPath: '/mina/anstallningar/preboarding',
        pathSuffix: '/preboarding',
        enabledConfig: 'preboarding',
      },
      {
        name: 'my-employment-preboarding',
        component: 'my-employment-preboarding.vue',
        defaultPath: '/mina/anstallningar/:employmentUid/preboarding',
        pathSuffix: '/:employmentUid/preboarding',
      },
      {
        name: 'my-employment-onboarding',
        component: 'my-employment-onboarding.vue',
        defaultPath: '/mina/anstallningar/:employmentUid/onboarding',
        pathSuffix: '/:employmentUid/onboarding',
      },
    ],
  },
  {
    id: 'my_assignments',
    routes: [{ name: 'my-assignments', component: 'my-assignments.vue', defaultPath: '/mina/uppdrag' }],
  },
  {
    id: 'my_invoices',
    routes: [{ name: 'my-invoices', component: 'my-invoices.vue', defaultPath: '/mina/fakturor' }],
  },
];

export const MODULE_ROUTE_NAMES = MODULE_ROUTE_NAME_SET;

export const PROTECTED_ROUTE_PREFIXES = ['admin-', 'test-'];

export const PROTECTED_ROUTE_NAMES = new Set([
  'login-internal',
  'logout',
  'error',
  'error404',
  'maintenance',
  'workspaces',
  'workspace',
  'examples',
]);

const ROUTING_CONTEXT = { settingsKeyPrefix: 'citizen_module' };

export const MERGE_APP_ROUTES_CONTEXT = {
  moduleRouteNames: MODULE_ROUTE_NAMES,
  protectedRouteNames: PROTECTED_ROUTE_NAMES,
  protectedRoutePrefixes: PROTECTED_ROUTE_PREFIXES,
};

export function getModuleSettingsKey(moduleId: string, suffix: string) {
  return getCoreModuleSettingsKey(moduleId, suffix, ROUTING_CONTEXT);
}

export { isModuleRoutingEnabled, resolveModuleBasePath, resolveModuleRoutePath };

export function getModuleRoutingSettingsSnapshot(settings: Record<string, unknown>) {
  return getCoreModuleRoutingSettingsSnapshot(settings, MODULE_REGISTRY, ROUTING_CONTEXT);
}

export const SHORTCUT_KEY_TO_MODULE_ID: Record<string, string> = {
  preboarding: 'my_employments',
  'min-anstallning': 'my_employments',
  'my-employments': 'my_employments',
  'my-cases': 'my_cases',
  'my-eservices': 'my_eservices',
  vrdnadshavare: 'my_children',
  'mina-barn': 'my_children',
  forening: 'my_associations',
  'mina-foreningar': 'my_associations',
  utbildning: 'my_educations',
  'my-educations': 'my_educations',
  'my-assignments': 'my_assignments',
  'my-invoices': 'my_invoices',
};

function isShortcutLinkForDisabledModule(shortcut: { key?: string; link?: string }, settings: Record<string, unknown>, appBase: string) {
  const rawLink = shortcut?.link;

  // API shortcuts may include the deployment base (/helix), unlike router paths.
  const base = appBase.replace(/\/$/, '');
  const pathname =
    typeof rawLink === 'string' && rawLink.startsWith('/') && !rawLink.startsWith('//')
      ? rawLink.split(/[?#]/)[0].replace(/\/$/, '')
      : null;
  const link = pathname && base && pathname.startsWith(`${base}/`) ? pathname.slice(base.length) : pathname;

  for (const module of MODULE_REGISTRY) {
    for (const route of module.routes) {
      if (!route.enabledConfig || isModuleRouteEnabled(settings, module.id, route, ROUTING_CONTEXT)) continue;
      // The deployed preboarding shortcut key is an identity, not a module ID.
      if (shortcut.key === route.name) return true;
      const paths = [route.defaultPath, resolveModuleRoutePath(settings, module.id, route, ROUTING_CONTEXT)];
      if (link && paths.includes(link)) return true;
    }

    if (isModuleRoutingEnabled(settings, module.id, ROUTING_CONTEXT)) {
      continue;
    }

    const primaryRoute = module.routes[0];
    const defaultBase = resolveModuleBasePath({}, module.id, primaryRoute.defaultPath, ROUTING_CONTEXT);
    const configuredBase = resolveModuleBasePath(settings, module.id, primaryRoute.defaultPath, ROUTING_CONTEXT);

    for (const basePath of [defaultBase, configuredBase]) {
      if (link && (link === basePath || link.startsWith(`${basePath}/`))) {
        return true;
      }
    }
  }

  return false;
}

export function isShortcutVisible(
  shortcut: { key?: string; link?: string },
  settings: Record<string, unknown> = {},
  appBase: string = import.meta.env.BASE_URL ?? '/',
) {
  if (isShortcutLinkForDisabledModule(shortcut, settings, appBase)) {
    return false;
  }

  const moduleId = shortcut?.key ? SHORTCUT_KEY_TO_MODULE_ID[shortcut.key] : undefined;

  if (moduleId && !isModuleRoutingEnabled(settings, moduleId, ROUTING_CONTEXT)) {
    return false;
  }

  return true;
}
